import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pricingService } from '../services/apiServices';

export const useActivePrices = () => {
  return useQuery({
    queryKey: ['prices', 'active'],
    queryFn: () => pricingService.getActivePrices(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCustomerPrice = (customerId: number | null, crateQuantity: number) => {
  return useQuery({
    queryKey: ['customerPrice', customerId, crateQuantity],
    queryFn: () => pricingService.getCustomerPrice(customerId ?? -1, crateQuantity),
    enabled: !!customerId && crateQuantity > 0,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const usePrices = (params?: any) => {
  return useQuery({
    queryKey: ['prices', params],
    queryFn: () => pricingService.getAll(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreatePrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => pricingService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prices'] });
    },
  });
};

export const useUpdatePrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => pricingService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prices'] });
    },
  });
};

export const useDeletePrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => pricingService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prices'] });
    },
  });
};

export const useTogglePriceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => pricingService.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prices'] });
    },
  });
};

export const usePricingSuggestions = (params?: { period?: number; target_margin?: number }) => {
  return useQuery({
    queryKey: ['pricing-suggestions', params],
    queryFn: () => pricingService.getPricingSuggestions(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useQuickRecommendation = () => {
  return useQuery({
    queryKey: ['quick-recommendation'],
    queryFn: () => pricingService.getQuickRecommendation(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
